import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Question } from "../models/Question";
import { MatterTypeQuestionService } from "../services/MatterTypeQuestionService";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { Answer } from "../models/Answer";
import { MatterService } from "../services/MatterService";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface QuestionContextProps {
  questions: Question[];
  selectedQuestion: Question | null;
  setSelectedQuestion: (questionId: string) => void;
  createAnswer: (
    questionId: string,
    answerData: Omit<Answer, "_id" | "createdAt" | "updatedAt" | "createdById">,
  ) => Promise<string>;
  updateQuestion: (
    questionId: string,
    updatedData: Partial<Omit<Question, "_id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  deleteQuestionById: (questionId: string) => void;
  loading: Loadings;
  error: Errors;
}

const QuestionContext = createContext<QuestionContextProps | undefined>(
  undefined,
);

export const useQuestion = (): QuestionContextProps => {
  const context = useContext(QuestionContext);
  if (!context) {
    throw new Error("useQuestion must be used within a QuestionProvider");
  }
  return context;
};

export const QuestionProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(
    null,
  );
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: true,
    details: true,
  });

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!user || !currentMatterId) return;
      setLoadings((prev) => ({ ...prev, list: true }));
      try {
        const fetchedQuestions =
          await MatterService.getQuestions(currentMatterId);
        setQuestions(fetchedQuestions);
        setLoadings((prev) => ({ ...prev, list: false }));
      } catch (err: any) {
        setErrors((prev) => ({ ...prev, list: err }));
        setLoadings((prev) => ({ ...prev, list: false }));
      }
    };

    fetchQuestions();
  }, [user, currentMatterId]);

  const setSelectedQuestionById = async (questionId: string) => {
    if (!currentMatterId) return;
    try {
      setLoadings((prev) => ({ ...prev, details: true }));
      const questionData = await MatterTypeQuestionService.getQuestion(
        currentMatterId,
        questionId,
      );
      setSelectedQuestion(questionData);
      setLoadings((prev) => ({ ...prev, details: false }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, details: err }));
      setLoadings((prev) => ({ ...prev, details: false }));
    }
  };

  const createAnswer = async (
    questionId: string,
    answerData: Omit<Answer, "_id" | "createdAt" | "updatedAt" | "createdById">,
  ): Promise<string> => {
    const newAnswerId = await MatterTypeQuestionService.createAnswer(
      currentMatterId!,
      questionId,
      {
        ...answerData,
      },
    );
    return newAnswerId;
  };

  const updateQuestion = async (
    questionId: string,
    updatedData: Partial<Omit<Question, "_id" | "createdAt" | "updatedAt">>,
  ) => {
    await MatterTypeQuestionService.updateQuestion(
      currentMatterId!,
      questionId,
      updatedData,
    );
    await setSelectedQuestionById(questionId);
  };

  const deleteQuestionById = async (questionId: string) => {
    if (!currentMatterId) return;
    await MatterTypeQuestionService.deleteQuestion(currentMatterId, questionId);
    setQuestions((prev) => prev.filter((q) => q._id !== questionId));
    setSelectedQuestion(null);
  };

  return (
    <QuestionContext.Provider
      value={{
        loading: loadings,
        error: errors,
        questions,
        selectedQuestion,
        createAnswer,
        updateQuestion,
        deleteQuestionById,
        setSelectedQuestion: setSelectedQuestionById,
      }}
    >
      {children}
    </QuestionContext.Provider>
  );
};
